const SCENE_IMAGE_CACHE_NAME = 'storyweave-scene-images-v1';
const FALLBACK_ORIGIN = 'http://localhost';

export type CachedImageSource = {
  src: string;
  objectUrl: boolean;
};

function cacheStorage() {
  return typeof caches === 'undefined' ? undefined : caches;
}

function cacheOrigin() {
  return typeof window === 'undefined' ? FALLBACK_ORIGIN : window.location.origin;
}

function canonicalRequest(url: string) {
  const normalized = new URL(url, cacheOrigin());
  normalized.search = '';
  normalized.hash = '';
  return new Request(normalized.toString());
}

async function openSceneImageCache() {
  const storage = cacheStorage();
  return storage ? storage.open(SCENE_IMAGE_CACHE_NAME) : undefined;
}

export async function loadCachedImage(url: string): Promise<CachedImageSource> {
  try {
    const cache = await openSceneImageCache();
    if (!cache) return { src: url, objectUrl: false };

    const key = canonicalRequest(url);
    const cached = await cache.match(key);
    const response = cached || await fetch(url, { credentials: 'same-origin' });
    if (response.status === 204 || !response.ok) return { src: '', objectUrl: false };

    if (!cached) await cache.put(key, response.clone());
    const objectUrl = URL.createObjectURL(await response.blob());
    return { src: objectUrl, objectUrl: true };
  } catch {
    return { src: url, objectUrl: false };
  }
}

export async function invalidateCachedImage(url: string): Promise<void> {
  try {
    const cache = await openSceneImageCache();
    if (cache) await cache.delete(canonicalRequest(url));
  } catch {
    // Cache invalidation is best effort and must not block image generation.
  }
}
